import React, { useState, useCallback, Suspense } from "react";
import CreateTeamSection from "./CreateTeamSection";
import { useParams, useNavigate } from "react-router-dom";
import useSWR, { mutate } from "swr";
import { useTheme } from "../config/ThemeContext";
import apiClient from "../services/apiClient";
//import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
const fetcher = (url) => apiClient.get(url).then(res => res.data);


// Lazy-load the edit dialog to defer non-critical chunk
const EditTeamDialog = React.lazy(() => import("./EditTeamDialog"));

// Memoized team item to prevent unnecessary re-renders
const TeamItem = React.memo(function TeamItem({ team, isEditing, onEdit, onDelete, onManagePlayers, seasonId, theme }) {
  return (
    <li className="border-b pb-4 last:border-0">
      <div className="flex justify-between items-center">
        <div>
          <h3 className={`font-semibold ${theme.neutral.text}`}>{team.name} ({team.code})</h3>
          <p className={`text-sm ${theme.neutral.lightText}`}>Coach: {team.headCoach}</p>
        </div>
        <div className="flex space-x-2">
          {isEditing ? null : (
            <>
			  <button onClick={() => onDelete(team.id)} className={`${theme.state.danger.text} ${theme.state.danger.border} ${theme.state.danger.hover} px-3 py-1 rounded`} title="Delete Team">
                🗑️
              </button>
              <button onClick={() => onEdit(team)} className={theme.buttons.secondary} title="Edit Team">
                ✏️ Edit
              </button>

              <button onClick={() => onManagePlayers(team.id)} className={theme.buttons.primary} title="Manage Players">
                🧑‍🤝‍🧑 Players
              </button>
              <a href={`/teams/${team.id}/report`} target="_blank" rel="noopener noreferrer" className={theme.buttons.secondary} title="O2 Team Registration Report">
                📋 O2
              </a>
              <a href={`/schedule/${seasonId}?teamId=${team.id}`} target="_blank" rel="noopener noreferrer" className={theme.buttons.secondary} title="Team Schedule">
                🕒 Schedule
              </a>
            </>
          )}
        </div>
      </div>
    </li>
  );
});

function TeamManagement() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { seasonId } = useParams();

const { data, error, isLoading } = useSWR(
  `/api/teams/season/${seasonId}`, 
  fetcher,
  { revalidateOnFocus: false }
);
  const isFetching = isLoading && !data;

  // Normalize response shape: array of teams or { season, teams }
  const { season, teams = [] } = data || {};
  const seasonName = season?.name || seasonId;
  const [editingTeam, setEditingTeam] = useState(null);

  const handleEdit = useCallback((team) => setEditingTeam(team), []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Delete this team?")) return;
    await apiClient.delete(`/api/teams/delete/${id}`);
    mutate(`/api/teams/season/${seasonId}`);
  }, [seasonId]);

  const handleCreateTeam = useCallback(async (newTeam) => {
    await apiClient.post(`/api/teams/create`, { ...newTeam, seasonId });
    mutate(`/api/teams/season/${seasonId}`);
  }, [seasonId]);

  const handleSave = useCallback(async (updated) => {
    await apiClient.put(`/api/teams/update/${updated.id}`, updated);
    mutate(`/api/teams/season/${seasonId}`);
    setEditingTeam(null);
  }, [seasonId]);

  const handleCancel = useCallback(() => setEditingTeam(null), []);

  const handleManagePlayers = useCallback((id) => navigate(`/player-management/${id}`), [navigate]);

  if (isFetching) {
    return (
      <div className="p-4">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4 animate-pulse" />
        <ul className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <li key={i} className="h-12 bg-gray-200 rounded animate-pulse" />
          ))}
        </ul>
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-red-500">Failed to load teams.</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
	      <div className="text-center mb-8">

              <h1 className={`text-3xl font-bold ${theme.neutral.text} mb-2`}>Teams for {seasonName || seasonId}</h1>
        <div className={`h-1 w-20 ${theme.primary.bg} mx-auto mt-4`}></div></div>
	        <div className={`bg-white rounded-xl shadow-md p-6 border-t-4 ${theme.neutral.border}`}>

      <ul className="space-y-4">
        {teams.map((team) => (
          <TeamItem
            key={team.id}
            team={team}
            isEditing={editingTeam?.id === team.id}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onManagePlayers={handleManagePlayers}
            seasonId={seasonId}
            theme={theme}
          />
        ))}
      </ul>
</div>
      {/* Lazy-loaded edit dialog */}
      <Suspense fallback={<div>Loading editor...</div>}>
        {editingTeam && (
          <EditTeamDialog
            team={editingTeam}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}
      </Suspense>

      {/* Create New Team Section */}
      <CreateTeamSection onCreate={handleCreateTeam} />
    </div>
  );
}

export default TeamManagement;